import { useEffect, useState } from 'react';
import api from '../../services/api';
import useEscapeClose from '../../hooks/useEscapeClose';
import CategoryCodeDetailModal from './CategoryCodeDetailModal';
import usePackOffers from '../../hooks/usePackOffers';
import PackCheckoutModal from '../stripe/PackCheckoutModal';
import QuizCodeCheckoutModal from '../stripe/QuizCodeCheckoutModal';
import CategoryCodeCheckoutModal from '../stripe/CategoryCodeCheckoutModal';
import ResultsExportModal from './ResultsExportModal';
import ThematiqueCodesList from './codes/ThematiqueCodesList';
import CategoryCodesList from './codes/CategoryCodesList';
import MonoCodesList from './codes/MonoCodesList';
import OpenCodesList from './codes/OpenCodesList';
import BuyPacksTab from './codes/BuyPacksTab';
import ReassignCodeModal from './codes/ReassignCodeModal';
import {
    Share2, Check, Loader2,
    ShoppingCart, Eye, Clock, KeyRound, Users,
    Layers, Ticket, FileDown,
} from 'lucide-react';

export default function ShareCodesPanel({ thematiqueId, thematiqueLabel, onClose }) {
    const [tab, setTab] = useState('codes'); // 'codes' | 'buy'
    const [codes, setCodes] = useState([]);
    const [stats, setStats] = useState({ available: 0, redeemed: 0, expired: 0 });
    const [loading, setLoading] = useState(true);
    const [pricing, setPricing] = useState(null);
    const [selectedDuree, setSelectedDuree] = useState('1m');
    const [themLabel, setThemLabel] = useState('');
    const [userStats, setUserStats] = useState(null);
    const [showPackModal, setShowPackModal] = useState(false);

    // Sub-onglet "Mes codes" : sélecteur de catégorie de code ('thematique' | 'category' | 'mono' | 'open')
    const [codesSubTab, setCodesSubTabRaw] = useState(() => {
        try { return localStorage.getItem('monquiz_codes_subtab') || 'thematique'; } catch { return 'thematique'; }
    });
    const setCodesSubTab = (v) => { setCodesSubTabRaw(v); try { localStorage.setItem('monquiz_codes_subtab', v); } catch {} };

    // Quiz codes (mono-sujet)
    const [quizCodes, setQuizCodes] = useState([]);
    const [qcSousSujets, setQcSousSujets] = useState([]); // available sous-sujets for generation
    const [qcSelectedSs, setQcSelectedSs] = useState('');
    const [qcLabel, setQcLabel] = useState('');
    const [showQcModal, setShowQcModal] = useState(false);
    const [qcMode, setQcMode] = useState('quiz'); // 'quiz' | 'apprentissage'

    // PDF export — Qualiopi-ready attestation of quiz results (état + génération dans <ResultsExportModal>).
    const [showExport, setShowExport] = useState(false);

    // Open training codes (free, no tracking per participant)
    const [openItems, setOpenItems] = useState([]);
    const [openCandidates, setOpenCandidates] = useState([]);
    const [openLoading, setOpenLoading] = useState(false);
    const loadOpenCodes = async () => {
        if (!thematiqueId) return;
        setOpenLoading(true);
        try {
            const { data } = await api.get('/custom/open-code/list.php', { params: { user_thematique_id: thematiqueId } });
            setOpenItems(data.data?.items || []);
            setOpenCandidates(data.data?.candidates || []);
        } catch {}
        setOpenLoading(false);
    };
    useEffect(() => { loadOpenCodes(); /* eslint-disable-next-line */ }, [thematiqueId]);

    // Reassign state — picker opened from an AVAILABLE code row (quiz or category).
    // { type: 'quiz'|'category', codeId, codeValue, currentTargetId, currentTargetLabel }
    const [reassignFor, setReassignFor] = useState(null);
    const [reassignBusy, setReassignBusy] = useState(false);
    const [reassignError, setReassignError] = useState(null);
    useEscapeClose(!!reassignFor && !reassignBusy, () => setReassignFor(null));
    const doReassign = async (newTargetId) => {
        if (!reassignFor || !newTargetId) return;
        setReassignBusy(true);
        setReassignError(null);
        try {
            if (reassignFor.type === 'quiz') {
                await api.post('/quiz-code/reassign.php', {
                    code_id: reassignFor.codeId,
                    new_sous_sujet_id: Number(newTargetId),
                });
                await loadQuizCodes();
            } else {
                await api.post('/category-code/reassign.php', {
                    code_id: reassignFor.codeId,
                    new_user_category_id: Number(newTargetId),
                });
                await loadCategoryCodes();
            }
            setReassignFor(null);
        } catch (e) {
            setReassignError(e.response?.data?.message || e.message || 'Erreur');
        }
        setReassignBusy(false);
    };

    // Category codes state
    const [catList, setCatList] = useState([]); // user_categories [{ id, label }]
    const [catSelectedId, setCatSelectedId] = useState('');
    const [catMode, setCatMode] = useState('quiz');
    const [catLabel, setCatLabel] = useState('');
    const [showCatcModal, setShowCatcModal] = useState(false);
    const [categoryCodes, setCategoryCodes] = useState([]);
    const [catDetailId, setCatDetailId] = useState(null);

    // Admin-configurable pack tariffs (pack_offers table)
    const { offers: catCodeOffers } = usePackOffers('category_code');
    const { offers: quizCodeOffers } = usePackOffers('quiz_code');

    const loadQuizCodes = async () => {
        try {
            const { data } = await api.get('/quiz-code/list.php', { params: { user_thematique_id: thematiqueId } });
            setQuizCodes(data.data?.codes || []);
            setQcSousSujets(data.data?.sous_sujets || []);
        } catch {}
    };

    const loadSousSujets = async () => {
        try {
            const { data } = await api.get(`/custom/list.php?thematique_id=${thematiqueId}`);
            const ready = (data.data?.sous_sujets || []).filter(s => s.status === 'READY');
            setQcSousSujets(ready);
            // user categories come in the same response (custom/list.php)
            setCatList(data.data?.categories || []);
        } catch {}
    };

    const loadCategoryCodes = async () => {
        try {
            const { data } = await api.get('/category-code/list.php', { params: { user_thematique_id: thematiqueId } });
            setCategoryCodes(data.data?.items || []);
        } catch {}
    };

    // Changement de mode d'un code AVAILABLE — mise à jour optimiste locale.
    const handleUpdateCatMode = async (codeId, newMode) => {
        try {
            await api.post('/category-code/list.php', { action: 'update_mode', code_id: codeId, mode: newMode });
            setCategoryCodes(prev => prev.map(c => c.id === codeId ? { ...c, mode: newMode } : c));
        } catch (err) {
            alert(err.response?.data?.message || 'Erreur');
        }
    };
    const handleUpdateQcMode = async (codeId, newMode) => {
        try {
            await api.post('/quiz-code/list.php', { action: 'update_mode', code_id: codeId, mode: newMode });
            setQuizCodes(prev => prev.map(c => c.id === codeId ? { ...c, mode: newMode } : c));
        } catch {}
    };

    useEffect(() => {
        if (!thematiqueId) return;
        loadCodes();
        loadPricing();
        loadQuizCodes();
        loadSousSujets();
        loadCategoryCodes();
        loadUserStats();
    }, [thematiqueId]);

    const loadCodes = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/share/codes.php?user_thematique_id=${thematiqueId}`);
            if (data.success) {
                setCodes(data.data.codes || []);
                setStats(data.data.stats || { available: 0, redeemed: 0, expired: 0 });
            }
        } catch {}
        setLoading(false);
    };

    const loadPricing = async () => {
        try {
            const { data } = await api.get(`/share/packs.php?user_thematique_id=${thematiqueId}`);
            if (data.success) {
                setPricing(data.data.pricing || {});
            }
        } catch {}
    };

    const loadUserStats = async () => {
        try {
            const { data } = await api.get(`/share/user-stats.php?user_thematique_id=${thematiqueId}`);
            if (data.success) setUserStats(data.data.users || []);
        } catch {}
    };

    const currentPricing = pricing?.[selectedDuree] || {};

    return (
        <>
        <div className="share-panel">
            {/* Header retiré — fourni par BaseModal parent (titre + croix de fermeture) */}

            {/* Sélecteur de méthode — 4 cartes (icône + compteur + nature de l'accès) */}
            <div className="share-methods">
                {[
                    { k: 'thematique', icon: Users, label: 'Thématique', sub: 'Compte requis', tone: 'req', count: codes.length },
                    { k: 'category', icon: Layers, label: 'Catégorie', sub: 'Sans compte', tone: 'ok', count: categoryCodes.length },
                    { k: 'mono', icon: KeyRound, label: 'Mono-sujet', sub: 'Sans compte', tone: 'ok', count: quizCodes.length },
                    { k: 'open', icon: Share2, label: 'Libre', sub: 'Anonyme', tone: 'anon', count: openItems.length },
                ].map(({ k, icon: Icon, label, sub, tone, count }) => (
                    <button key={k} type="button" className={`share-method ${codesSubTab === k ? 'is-active' : ''}`} onClick={() => setCodesSubTab(k)}>
                        <span className="share-method__icon"><Icon size={15} /></span>
                        <span className="share-method__body">
                            <span className="share-method__top">
                                <span className="share-method__label">{label}</span>
                                {count > 0 && <span className="share-method__count">{count}</span>}
                            </span>
                            <span className={`share-method__sub share-method__sub--${tone}`}>{sub}</span>
                        </span>
                    </button>
                ))}
            </div>

            {/* Encart descriptif de la méthode sélectionnée */}
            <div className="share-method-desc">
                <span className="share-method-desc__icon">
                    {(() => { const Ic = { thematique: Users, category: Layers, mono: KeyRound, open: Share2 }[codesSubTab] || Layers; return <Ic size={14} />; })()}
                </span>
                <div className="share-method-desc__txt">
                    {codesSubTab === 'thematique' && <><b>Partage par thématique</b> — il accède à <b>toute la thématique</b>, sur son propre compte. Il peut faire les quiz, mais pas modifier le contenu. Tu suis chaque participant (prénom / email), ses quiz, scores et dates dans <b>Résultats › Suivi des résultats</b>.</>}
                    {codesSubTab === 'category' && <><b>Partage par catégorie</b> — un code donne accès à <b>tous les sujets d'une catégorie</b>, sans créer de compte. Pour chaque participant tu suis <b>qui c'est</b> (prénom / email), <b>quels quiz</b> il a faits, <b>ses scores</b> et <b>quand</b> — récap dans <b>Résultats › Suivi des résultats</b>.</>}
                    {codesSubTab === 'mono' && <><b>Partage mono-sujet</b> — un code donne accès à <b>un seul quiz</b>, sans créer de compte. Idéal formation / évaluation. Tu suis chaque participant et son score dans <b>Résultats › Suivi des résultats</b>.</>}
                    {codesSubTab === 'open' && <><b>Entraînement libre mono-sujet</b> — un lien public, gratuit et <b>anonyme</b>. Tu colles le lien où tu veux, n'importe qui peut faire le quiz (correction après chaque question). Tu ne sauras pas qui a répondu : juste 2 compteurs (commencés / terminés).</>}
                </div>
            </div>

            {/* Stats du type de code sélectionné en haut (codesSubTab) */}
            {(() => {
                // Libre : compteurs propres (liens · démarrées · complétées), pas de "disponibles/utilisés".
                if (codesSubTab === 'open') {
                    const started = openItems.reduce((s, o) => s + (o.open_started_count || 0), 0);
                    const completed = openItems.reduce((s, o) => s + (o.open_completed_count || 0), 0);
                    return (
                        <div className="share-panel__stats">
                            <div className="share-panel__stat"><Share2 size={14} /><span>{openItems.length} lien{openItems.length > 1 ? 's' : ''}</span></div>
                            <div className="share-panel__stat"><Eye size={14} /><span>{started} d&eacute;marr&eacute;e{started > 1 ? 's' : ''}</span></div>
                            <div className="share-panel__stat"><Check size={14} /><span>{completed} compl&eacute;t&eacute;e{completed > 1 ? 's' : ''}</span></div>
                        </div>
                    );
                }
                let dispo, used;
                if (codesSubTab === 'category') {
                    dispo = categoryCodes.filter(c => c.status === 'AVAILABLE').length;
                    used = categoryCodes.filter(c => c.status === 'IN_PROGRESS' || c.status === 'FINISHED').length;
                } else if (codesSubTab === 'mono') {
                    dispo = quizCodes.filter(c => c.status === 'AVAILABLE').length;
                    used = quizCodes.filter(c => c.status === 'IN_PROGRESS' || c.status === 'FINISHED').length;
                } else { // thematique
                    dispo = stats.available || 0;
                    used = stats.redeemed || 0;
                }
                const expiredCount = stats.expired || 0;
                return (
                    <div className="share-panel__stats">
                        <div className="share-panel__stat">
                            <Ticket size={14} />
                            <span>{dispo} disponible{dispo > 1 ? 's' : ''}</span>
                        </div>
                        <div className="share-panel__stat">
                            <Eye size={14} />
                            <span>{used} utilis&eacute;{used > 1 ? 's' : ''}</span>
                        </div>
                        {codesSubTab === 'thematique' && (
                            <div className="share-panel__stat" title="Seuls les codes d'acc&egrave;s th&eacute;matique expirent (dur&eacute;e d'abonnement d&eacute;pass&eacute;e)">
                                <Clock size={14} />
                                <span>{expiredCount} acc&egrave;s expir&eacute;{expiredCount > 1 ? 's' : ''}</span>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Tabs */}
            <div className="share-panel__tabs">
                <button className={`share-panel__tab ${tab === 'codes' ? 'share-panel__tab--active' : ''}`}
                    onClick={() => setTab('codes')}>
                    Mes codes
                </button>
                <button className={`share-panel__tab ${tab === 'buy' ? 'share-panel__tab--active' : ''}`}
                    onClick={() => setTab('buy')}>
                    <ShoppingCart size={14} /> Acheter des codes
                </button>
            </div>

            {/* === TAB: Codes list === */}
            {tab === 'codes' && (
                <div className="share-panel__codes">
                    {/* Export PDF action : first thing in the tab, before any list. */}
                    <div className="share-panel__codes-actions">
                        <button
                            className="btn btn-sm btn-ghost share-panel__export-btn"
                            onClick={() => setShowExport(true)}
                            title="Exporter les résultats des participants (attestation PDF Qualiopi)">
                            <FileDown size={14} /> Exporter PDF
                        </button>
                    </div>
                    {loading ? (
                        <div className="share-panel__loading"><Loader2 size={20} className="spin" /></div>
                    ) : (<>
                        {codesSubTab === 'thematique' && (
                            <ThematiqueCodesList codes={codes} userStats={userStats} onReload={loadCodes} />
                        )}
                        {codesSubTab === 'category' && (
                            <CategoryCodesList
                                categoryCodes={categoryCodes}
                                onReload={loadCategoryCodes}
                                onUpdateMode={handleUpdateCatMode}
                                onDetail={setCatDetailId}
                                onReassign={(c) => { setReassignError(null); setReassignFor({ type: 'category', codeId: c.id, codeValue: c.code, currentTargetId: c.category_id ?? c.user_category_id, currentTargetLabel: c.category_label }); }}
                            />
                        )}
                        {codesSubTab === 'mono' && (
                            <MonoCodesList
                                quizCodes={quizCodes}
                                onReload={loadQuizCodes}
                                onUpdateMode={handleUpdateQcMode}
                                onReassign={(c) => { setReassignError(null); setReassignFor({ type: 'quiz', codeId: c.id, codeValue: c.code, currentTargetId: c.sous_sujet_id, currentTargetLabel: c.sous_sujet_label }); }}
                            />
                        )}
                        {codesSubTab === 'open' && (
                            <OpenCodesList items={openItems} candidates={openCandidates} loading={openLoading} onReload={loadOpenCodes} />
                        )}
                    </>)}
                </div>
            )}

            {/* === TAB: Buy packs === */}
            {tab === 'buy' && (
                <BuyPacksTab
                    codesSubTab={codesSubTab}
                    onGoToCodes={() => setTab('codes')}
                    pricing={pricing} currentPricing={currentPricing}
                    selectedDuree={selectedDuree} setSelectedDuree={setSelectedDuree}
                    themLabel={themLabel} setThemLabel={setThemLabel}
                    onBuyPack={() => setShowPackModal(true)}
                    catList={catList} catSelectedId={catSelectedId} setCatSelectedId={setCatSelectedId}
                    catMode={catMode} setCatMode={setCatMode} catLabel={catLabel} setCatLabel={setCatLabel}
                    catCodeOffers={catCodeOffers} onBuyCategory={() => setShowCatcModal(true)}
                    qcSousSujets={qcSousSujets} qcSelectedSs={qcSelectedSs} setQcSelectedSs={setQcSelectedSs}
                    qcMode={qcMode} setQcMode={setQcMode} qcLabel={qcLabel} setQcLabel={setQcLabel}
                    quizCodeOffers={quizCodeOffers} onBuyQuiz={() => setShowQcModal(true)}
                />
            )}

        </div>

        {showPackModal && pricing && (
            <PackCheckoutModal
                thematiqueId={thematiqueId}
                thematiqueLabel={thematiqueLabel}
                pricing={pricing}
                initialDuree={selectedDuree}
                initialLabel={themLabel}
                onClose={() => setShowPackModal(false)}
                onSuccess={() => { loadCodes(); loadPricing(); }}
            />
        )}

        {showQcModal && qcSelectedSs && (
            <QuizCodeCheckoutModal
                thematiqueId={thematiqueId}
                sousSujetId={Number(qcSelectedSs)}
                sousSujetLabel={qcSousSujets.find(s => String(s.sous_sujet_id || s.id) === String(qcSelectedSs))?.sous_sujet_label || qcSousSujets.find(s => String(s.sous_sujet_id || s.id) === String(qcSelectedSs))?.label || ''}
                label={qcLabel}
                mode={qcMode}
                onClose={() => setShowQcModal(false)}
                onSuccess={() => { loadQuizCodes(); }}
            />
        )}

        {showCatcModal && catSelectedId && (
            <CategoryCodeCheckoutModal
                thematiqueId={thematiqueId}
                categoryId={Number(catSelectedId)}
                categoryLabel={catList.find(c => String(c.id) === String(catSelectedId))?.label || ''}
                label={catLabel}
                mode={catMode}
                onClose={() => setShowCatcModal(false)}
                onSuccess={() => { loadCategoryCodes(); }}
            />
        )}

        {catDetailId && (
            <CategoryCodeDetailModal
                item={categoryCodes.find(c => c.id === catDetailId)}
                onClose={() => setCatDetailId(null)}
            />
        )}

        {/* Export PDF — composant reutilisable */}
        <ResultsExportModal
          open={showExport}
          onClose={() => setShowExport(false)}
          thematiqueId={thematiqueId}
          codes={codes}
          categoryCodes={categoryCodes}
          quizCodes={quizCodes}
          userStats={userStats}
        />

        {reassignFor && (
            <ReassignCodeModal
                reassignFor={reassignFor}
                busy={reassignBusy}
                error={reassignError}
                onClose={() => setReassignFor(null)}
                onReassign={doReassign}
                qcSousSujets={qcSousSujets}
                catList={catList}
            />
        )}
        </>
    );
}
